<template>
  <div ref="appointmentSelectSpeciality">
    <div
      data-qa="appointment_selected_speciality_card"
      class="d-flex align-center justify-space-between"
    >
      <div class="pr-4">
        <div
          data-qa="appointment_selected_speciality_card_name"
          class="text-body-1 text--text"
        >
          {{ selectSpecialityTitle }}
        </div>
        <div
          data-qa="appointment_selected_speciality_card_description"
          class="text-body-2 text-secondary--text"
        >
          {{ selectSpecialitySubtitle }}
        </div>
      </div>
      <v-btn
        v-if="formattedWorkplaceList.length > 1"
        data-qa="appointment_selected_speciality_card_icon"
        color="icon-secondary--text"
        height="32"
        width="32"
        icon
        @click="isModalVisible = true"
      >
        <span class="ui-icon-pen" />
      </v-btn>
    </div>
    <component
      :is="isMobile ? CoreBottomSheet : CoreDialog"
      v-model="isModalVisible"
      v-bind="isMobile
        ? {
          minHeight: '65dvh',
        } : {
          size: 'lg',
          minHeight: 496,
        }"
      title="Выберите специальность врача"
      :persistent="!selectedWorkplace"
      eager
    >
      <template
        v-if="selectedWorkplace"
        #prependBtn="{ btnProps }"
      >
        <v-btn
          v-bind="btnProps"
          @click="isModalVisible = false"
        >
          <span class="ui-icon-close-not-a-circle" />
        </v-btn>
      </template>
      <template #default="{ xSpaceClass, counterXSpaceClass }">
        <ListSkeleton
          v-if="nearestSlotsStore.isPending"
          data-qa="appointment_patient_list_skeleton"
        />
        <v-list
          v-else
          :class="counterXSpaceClass"
          data-qa="appointment_speciality_list"
        >
          <v-list-item
            v-for="workplace in formattedWorkplaceList"
            :key="workplace.id"
            data-qa="appointment_speciality_list_speciality"
            :class="xSpaceClass"
            :disabled="workplace.isDisabledWorkplace"
            @click="handleClickWorkplaceItem(workplace)"
          >
            <v-list-item-content>
              <v-list-item-title class="d-flex align-end">
                <div
                  data-qa="appointment_speciality_list_speciality_name"
                  class="text-body-1"
                >
                  {{ workplace.speciality.name }}
                </div>
              </v-list-item-title>
              <v-list-item-subtitle
                class="text-body-2 text-break mt-1"
              >
                <div>
                  <span data-qa="appointment_speciality_list_speciality_price">{{ workplace.price }}</span>
                  <template v-if="workplace.specialityAges">
                    • <span data-qa="appointment_speciality_list_speciality_ages">{{ workplace.specialityAges }}</span>
                  </template>
                </div>
                <div
                  v-if="workplace.warningDate"
                  class="text-body-2 text-warning--text mt-1"
                >
                  На выбранное время запись недоступна.
                  <br> Ближайшее к нему – <span data-qa="appointment_speciality_list_speciality_warning_date">{{ workplace.warningDate }}</span>
                </div>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-icon class="align-self-center my-auto">
              <v-icon color="text-secondary">
                ui-icon-arrow-right
              </v-icon>
            </v-list-item-icon>
          </v-list-item>
        </v-list>
      </template>
    </component>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue'
import { storeToRefs } from 'pinia'
import { format, parse } from 'date-fns'
import { ru } from 'date-fns/locale'
import { getFormattedPrice } from 'www/utils'
import {
  useAppointmentDataStore,
  useAuthorizationStore, // todo(useAuthMt) - удалить
  useDoctorDataStore,
  useCalendarDataStore,
  useNearestSlotsStore,
} from 'www/vue_components/common/AppointmentPage/stores'
import useFamilyProfilesStore from 'www/vue_components/common/FamilyProfiles/stores/useFamilyProfilesStore'
import { getHasSuitableAgeInWorkplace, getSpecialityAgesText } from 'www/vue_components/common/AppointmentPage/functions'
import { DoctorWorkplace } from 'www/vue_components/common/AppointmentPage/interfaces'
import ListSkeleton from 'www/vue_components/common/ListSkeleton/ListSkeleton.vue'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import CoreBottomSheet from 'src/core/CoreBottomSheet/CoreBottomSheet.vue'

type FormattedWorkplace = DoctorWorkplace & {
  isDisabledWorkplace: boolean
  specialityAges: string
  price: string
  warningDate: string
}

const isMobile = window.MOBILE_VERSION

const appointmentDataStore = useAppointmentDataStore()
const { initAdditionalData, useAuthMt } = storeToRefs(appointmentDataStore)

const authorizationStore = useAuthorizationStore() // todo(useAuthMt) - удалить
const { isPhoneConfirmed } = storeToRefs(authorizationStore) // todo(useAuthMt) - удалить

const doctorDataStore = useDoctorDataStore()
const { selectedDoctorSpecialityId, doctorData, isWorkplaceSelected } = storeToRefs(doctorDataStore)
const {
  updateAppointmentSpecialitiesRef,
  setStateSelectedDoctorSpecialityId,
  updateStateSelectedDoctorSpecialityId,
} = doctorDataStore

const familyProfilesStore = useFamilyProfilesStore()
const { isSelectedPatient, selectedPatient } = storeToRefs(familyProfilesStore)

const calendarDataStore = useCalendarDataStore()
const { isCalendarDateSelected } = storeToRefs(calendarDataStore)

const nearestSlotsStore = useNearestSlotsStore()

const selectedWorkplace = computed<DoctorWorkplace | undefined>(
  () => doctorData.value.workplaces
    .find(
      (workplace: DoctorWorkplace) => workplace.speciality.id === (selectedDoctorSpecialityId.value as number | null),
    ),
)

const appointmentSelectSpeciality = ref<HTMLDivElement | null>(null)

const isModalVisibleDefault = ref(false)
const isModalVisible = computed({
  get() {
    return (
      (isPhoneConfirmed.value || useAuthMt.value)
      && isSelectedPatient.value
      && !selectedWorkplace.value
    ) || isModalVisibleDefault.value
  },
  set(val) {
    isModalVisibleDefault.value = val
  },
})

function formatWorkplaceV2(workplace: DoctorWorkplace): FormattedWorkplace {
  const hasSuitableAge = getHasSuitableAgeInWorkplace({
    workplace,
    ageNumberPatient: selectedPatient.value.ageNumber,
  })
  const { priceOnline, priceMedtochkaTelemed } = workplace.priceList
  const price: number = priceOnline ?? priceMedtochkaTelemed ?? 0

  let priceType: 'mt' | 'lpu' | 'both'

  if (typeof priceMedtochkaTelemed === 'number' && typeof priceOnline === 'number') {
    priceType = 'both'
  } else {
    priceType = typeof priceOnline === 'number' ? 'lpu' : 'mt'
  }

  let warningDate = ''

  if (priceType !== 'both' && !nearestSlotsStore.isSlotSelected(priceType)) {
    const slotData = nearestSlotsStore.data[priceType]

    if (slotData) {
      const nearestSlotDateFormatted = parse(slotData.nearest_dt, 'yyyy-MM-dd HH:mm', new Date(), { locale: ru })

      warningDate = format(nearestSlotDateFormatted, 'd\u00A0MMMM,\u00A0HH:mm', { locale: ru })
    }
  }

  return {
    ...workplace,
    isDisabledWorkplace: !hasSuitableAge,
    specialityAges: getSpecialityAgesText(workplace.age, { withBrackets: false }),
    price: getFormattedPrice(price) ?? '',
    warningDate,
  }
}

const formattedWorkplaceList = computed<FormattedWorkplace[]>(
  () => doctorData.value.workplaces
    .map((workplace: DoctorWorkplace) => formatWorkplaceV2(workplace))
    .filter((workplace: FormattedWorkplace) => !workplace.isDisabledWorkplace),
)

const formattedSelectedWorkplace = computed<FormattedWorkplace | null>(
  () => (selectedWorkplace.value ? formatWorkplaceV2(selectedWorkplace.value) : null),
)

const selectSpecialityTitle = computed(
  () => (formattedSelectedWorkplace.value ? formattedSelectedWorkplace.value.speciality.name : 'Выберите специальность'),
)

const selectSpecialitySubtitle = computed(
  () => {
    if (!formattedSelectedWorkplace.value) {
      return undefined
    }

    let text = formattedSelectedWorkplace.value.price

    if (formattedSelectedWorkplace.value.specialityAges) {
      text += ` • ${formattedSelectedWorkplace.value.specialityAges}`
    }

    return text
  },
)

async function changeSpeciality(id: number) {
  const wasWorkplaceSelected = isWorkplaceSelected.value

  setStateSelectedDoctorSpecialityId(id)

  if (wasWorkplaceSelected) {
    await nearestSlotsStore.changePlaceTypeIfNeeded()
  }
}

async function handleClickWorkplaceItem(workplace: DoctorWorkplace) {
  await changeSpeciality(workplace.speciality.id)

  isModalVisible.value = false
}

watch(selectedPatient, async () => {
  if (formattedSelectedWorkplace.value?.isDisabledWorkplace) {
    updateStateSelectedDoctorSpecialityId(null)
  }

  if (!selectedWorkplace.value && formattedWorkplaceList.value.length === 1) {
    await changeSpeciality(formattedWorkplaceList.value[0].speciality.id)
  }
})

if (initAdditionalData.value?.specialityId) {
  changeSpeciality(initAdditionalData.value.specialityId)
}

watch(isModalVisible, async () => {
  if (isModalVisible.value && isCalendarDateSelected.value) {
    await nearestSlotsStore.update()
  } else {
    nearestSlotsStore.data = {}
  }
})

onMounted(() => {
  updateAppointmentSpecialitiesRef(appointmentSelectSpeciality.value)
})
</script>
